import { oklchToHex, oklchToCss } from "@/document/model/color";
import type { OklchColor } from "@/document/model/color";
import {
  normalizeFillStack,
  normalizeStrokeStack,
  normalizeGradientPaint,
  sampleGradientPaint,
} from "@/document/model/styles";
import type { Paint, NodeEffect, LinearGradientPaint, RadialGradientPaint } from "@/document/model/styles";
import type { SceneNode } from "@/document/model/nodes";
import { frameAutoLayoutMode, frameCornerRadii } from "@/document/model/nodes";

function fmt(n: number): string {
  const r = Math.round(n * 100) / 100;
  return r === 0 ? "0" : String(r);
}

function fmtPx(n: number): string {
  return `${fmt(n)}px`;
}

function colorToCss(color: OklchColor): string {
  return oklchToCss(color);
}

function colorToHex(color: OklchColor): string {
  return oklchToHex(color);
}

function linearGradientToCss(paint: LinearGradientPaint): string {
  const normalized = normalizeGradientPaint(paint);

  if (normalized.colorSpace === "oklch") {
    // Use CSS color-interpolation in oklch for true OKLCH gradient
    const sampledStops = sampleGradientPaint(normalized, 32);
    const stops = sampledStops
      .map(s => `${colorToCss(s.color)} ${fmt(s.position * 100)}%`)
      .join(", ");
    return `linear-gradient(in oklch ${fmt(normalized.angle)}deg, ${stops})`;
  }

  const stops = normalized.stops
    .map(s => `${colorToCss(s.color)} ${fmt(s.position * 100)}%`)
    .join(", ");
  return `linear-gradient(${fmt(normalized.angle)}deg, ${stops})`;
}

function radialGradientToCss(paint: RadialGradientPaint): string {
  const normalized = normalizeGradientPaint(paint);
  const center = normalized.center ?? { x: 0.5, y: 0.5 };
  const centerPct = `${fmt(center.x * 100)}% ${fmt(center.y * 100)}%`;

  if (normalized.colorSpace === "oklch") {
    const sampledStops = sampleGradientPaint(normalized, 32);
    const stops = sampledStops
      .map(s => `${colorToCss(s.color)} ${fmt(s.position * 100)}%`)
      .join(", ");
    return `radial-gradient(in oklch circle at ${centerPct}, ${stops})`;
  }

  const stops = normalized.stops
    .map(s => `${colorToCss(s.color)} ${fmt(s.position * 100)}%`)
    .join(", ");
  return `radial-gradient(circle at ${centerPct}, ${stops})`;
}

function paintToCssBackground(paint: Paint): string | null {
  if (paint.type === "solid") {
    const hex = colorToHex(paint.color);
    if (paint.color.alpha < 1) {
      return colorToCss(paint.color);
    }
    return hex;
  }
  if (paint.type === "linearGradient") {
    return linearGradientToCss(paint);
  }
  if (paint.type === "radialGradient") {
    return radialGradientToCss(paint);
  }
  if (paint.type === "image") {
    return "/* image fill */";
  }
  return null;
}

function effectsToCssBoxShadow(effects: NodeEffect[]): string[] {
  const shadows: string[] = [];
  for (const effect of effects) {
    if (!effect.visible) continue;
    if (effect.type === "dropShadow") {
      const color = colorToCss(effect.color);
      shadows.push(`${fmtPx(effect.offsetX)} ${fmtPx(effect.offsetY)} ${fmtPx(effect.blur)} ${fmtPx(effect.spread)} ${color}`);
    } else if (effect.type === "innerShadow") {
      const color = colorToCss(effect.color);
      shadows.push(`inset ${fmtPx(effect.offsetX)} ${fmtPx(effect.offsetY)} ${fmtPx(effect.blur)} ${fmtPx(effect.spread)} ${color}`);
    }
  }
  return shadows;
}

function effectsToCssFilter(effects: NodeEffect[]): string[] {
  const filters: string[] = [];
  for (const effect of effects) {
    if (!effect.visible) continue;
    if (effect.type === "layerBlur") {
      filters.push(`blur(${fmtPx(effect.blur)})`);
    }
  }
  return filters;
}

function effectsToCssBackdropFilter(effects: NodeEffect[]): string[] {
  const filters: string[] = [];
  for (const effect of effects) {
    if (!effect.visible) continue;
    if (effect.type === "backgroundBlur") {
      filters.push(`blur(${fmtPx(effect.blur)})`);
    }
  }
  return filters;
}

function primitiveCornerRadii(node: SceneNode): [number, number, number, number] | null {
  if (node.type === "frame") {
    const radii = frameCornerRadii(node);
    if (radii.some(r => r > 0)) return radii;
    return null;
  }
  if (node.type === "primitive") {
    const prim = node.primitive;
    if (prim.kind === "rectangle") {
      const radii = prim.cornerRadii;
      if (radii.some((r: number) => r > 0)) return radii;
    }
  }
  return null;
}

export interface CssLine {
  property: string;
  value: string;
  comment?: string;
}

export interface CssSection {
  label: string;
  lines: CssLine[];
}

export function generateNodeCss(node: SceneNode): CssSection[] {
  const sections: CssSection[] = [];
  const style = node.style;
  const fills = normalizeFillStack(style);
  const strokes = normalizeStrokeStack(style);
  const effects = style.effects ?? [];

  // Position & Size
  const posLines: CssLine[] = [
    { property: "width", value: fmtPx(node.bounds.width) },
    { property: "height", value: fmtPx(node.bounds.height) },
  ];
  sections.push({ label: "Position & Size", lines: posLines });

  // Fills
  if (fills.length > 0) {
    const fillLines: CssLine[] = [];
    if (fills.length === 1) {
      const bg = paintToCssBackground(fills[0]!);
      if (bg) {
        const prop = fills[0]!.type === "solid" ? "background-color" : "background";
        fillLines.push({ property: prop, value: bg });
      }
    } else {
      // Multiple fills: stack as multiple backgrounds (bottom-to-top for CSS)
      const bgs = fills.map(f => paintToCssBackground(f)).filter(Boolean) as string[];
      if (bgs.length > 0) {
        fillLines.push({ property: "background", value: bgs.join(",\n      ") });
      }
    }
    if (fillLines.length > 0) {
      sections.push({ label: "Fill", lines: fillLines });
    }
  }

  // Strokes
  if (strokes.length > 0) {
    const strokeLines: CssLine[] = [];
    for (const stroke of strokes) {
      const paint = stroke.paint;
      if (paint.type === "solid") {
        const color = colorToHex(paint.color);
        const align = stroke.align ?? "center";
        if (align === "center") {
          strokeLines.push({ property: "border", value: `${fmtPx(stroke.width)} solid ${color}` });
        } else if (align === "inside") {
          strokeLines.push({ property: "box-shadow", value: `inset 0 0 0 ${fmtPx(stroke.width)} ${color}` });
        } else if (align === "outside") {
          strokeLines.push({ property: "outline", value: `${fmtPx(stroke.width)} solid ${color}` });
          strokeLines.push({ property: "outline-offset", value: "0" });
        }
      } else if (paint.type === "linearGradient" || paint.type === "radialGradient") {
        strokeLines.push({ property: "border", value: `${fmtPx(stroke.width)} solid /* gradient stroke */` });
      }
    }
    if (strokeLines.length > 0) {
      sections.push({ label: "Stroke", lines: strokeLines });
    }
  }

  // Effects
  const shadows = effectsToCssBoxShadow(effects);
  const filters = effectsToCssFilter(effects);
  const backdropFilters = effectsToCssBackdropFilter(effects);
  if (shadows.length > 0 || filters.length > 0 || backdropFilters.length > 0) {
    const effectLines: CssLine[] = [];
    if (shadows.length > 0) {
      effectLines.push({ property: "box-shadow", value: shadows.join(",\n      ") });
    }
    if (filters.length > 0) {
      effectLines.push({ property: "filter", value: filters.join(" ") });
    }
    if (backdropFilters.length > 0) {
      effectLines.push({ property: "backdrop-filter", value: backdropFilters.join(" ") });
    }
    sections.push({ label: "Effects", lines: effectLines });
  }

  // Opacity
  if (style.opacity < 1) {
    sections.push({ label: "Appearance", lines: [{ property: "opacity", value: String(fmt(style.opacity)) }] });
  }

  // Corner radius
  const cornerRadii = primitiveCornerRadii(node);
  if (cornerRadii) {
    const [tl, tr, br, bl] = cornerRadii;
    const unique = new Set(cornerRadii);
    const value = unique.size === 1
      ? fmtPx(tl!)
      : `${fmtPx(tl!)} ${fmtPx(tr!)} ${fmtPx(br!)} ${fmtPx(bl!)}`;
    sections.push({ label: "Border Radius", lines: [{ property: "border-radius", value }] });
  }

  // Auto layout
  if (node.type === "frame") {
    const mode = frameAutoLayoutMode(node);
    if (mode === "flex" && node.flexLayout) {
      const layout = node.flexLayout;
      const layoutLines: CssLine[] = [
        { property: "display", value: "flex" },
        { property: "flex-direction", value: layout.direction },
        { property: "justify-content", value: layout.justifyContent },
        { property: "align-items", value: layout.alignItems },
      ];
      if (layout.gap > 0) {
        layoutLines.push({ property: "gap", value: fmtPx(layout.gap) });
      }
      const { top, right, bottom, left } = layout.padding;
      if (top > 0 || right > 0 || bottom > 0 || left > 0) {
        if (top === right && right === bottom && bottom === left) {
          layoutLines.push({ property: "padding", value: fmtPx(top) });
        } else {
          layoutLines.push({ property: "padding", value: `${fmtPx(top)} ${fmtPx(right)} ${fmtPx(bottom)} ${fmtPx(left)}` });
        }
      }
      sections.push({ label: "Layout", lines: layoutLines });
    }
  }

  // Typography
  if (node.type === "text" && node.text) {
    const textModel = node.text;
    const ts = textModel.style;
    const typographyLines: CssLine[] = [];
    if (ts.fontFamily) typographyLines.push({ property: "font-family", value: `"${ts.fontFamily}", sans-serif` });
    if (ts.fontSize) typographyLines.push({ property: "font-size", value: fmtPx(ts.fontSize) });
    if (ts.fontWeight) typographyLines.push({ property: "font-weight", value: String(ts.fontWeight) });
    if (ts.lineHeight) {
      let lh: string;
      if (ts.lineHeight === "auto") {
        lh = "normal";
      } else if (typeof ts.lineHeight === "number") {
        lh = fmtPx(ts.lineHeight);
      } else if (ts.lineHeight.unit === "px") {
        lh = fmtPx(ts.lineHeight.value);
      } else {
        lh = `${fmt(ts.lineHeight.value)}%`;
      }
      typographyLines.push({ property: "line-height", value: lh });
    }
    if (ts.letterSpacing !== undefined && ts.letterSpacing !== 0) {
      typographyLines.push({ property: "letter-spacing", value: `${fmt(ts.letterSpacing / (ts.fontSize ?? 12))}em` });
    }
    if (ts.horizontalAlign && ts.horizontalAlign !== "LEFT") {
      const alignMap: Record<string, string> = {
        CENTER: "center",
        RIGHT: "right",
        JUSTIFIED: "justify",
      };
      const align = alignMap[ts.horizontalAlign];
      if (align) typographyLines.push({ property: "text-align", value: align });
    }
    // Text color from the first fill
    const textFills = normalizeFillStack(style);
    const firstFill = textFills[0];
    if (firstFill?.type === "solid") {
      typographyLines.push({ property: "color", value: colorToHex(firstFill.color) });
    }
    if (typographyLines.length > 0) {
      sections.push({ label: "Typography", lines: typographyLines });
    }
  }

  return sections;
}

export function sectionsToCssString(sections: CssSection[]): string {
  const parts: string[] = [];
  for (const section of sections) {
    parts.push(`/* ${section.label} */`);
    for (const line of section.lines) {
      parts.push(`${line.property}: ${line.value};`);
    }
    parts.push("");
  }
  return parts.join("\n").trimEnd();
}

// --------------- Tailwind ---------------

function closestTailwindRounded(px: number): string {
  if (px === 0) return "rounded-none";
  if (px <= 2) return "rounded-sm";
  if (px <= 4) return "rounded";
  if (px <= 6) return "rounded-md";
  if (px <= 8) return "rounded-lg";
  if (px <= 12) return "rounded-xl";
  if (px <= 16) return "rounded-2xl";
  if (px <= 24) return "rounded-3xl";
  if (px >= 9999) return "rounded-full";
  return `rounded-[${fmtPx(px)}]`;
}

function closestTailwindOpacity(opacity: number): string {
  const pct = Math.round(opacity * 100);
  const standard = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];
  if (standard.includes(pct)) return `opacity-${pct}`;
  return `opacity-[${fmt(opacity)}]`;
}

function colorToTailwindArbitrary(color: OklchColor, prefix: string): string {
  const hex = colorToHex(color);
  if (color.alpha < 1) {
    // Use OKLCH arbitrary value for alpha support
    const css = oklchToCss(color);
    return `${prefix}-[${css.replace(/\s+/g, "_")}]`;
  }
  return `${prefix}-[${hex}]`;
}

export function generateNodeTailwind(node: SceneNode): string[] {
  const classes: string[] = [];
  const style = node.style;
  const fills = normalizeFillStack(style);
  const strokes = normalizeStrokeStack(style);
  const effects = style.effects ?? [];

  // Size (arbitrary values)
  classes.push(`w-[${fmtPx(node.bounds.width)}]`);
  classes.push(`h-[${fmtPx(node.bounds.height)}]`);

  // Fill
  if (fills.length === 1) {
    const fill = fills[0]!;
    if (fill.type === "solid") {
      classes.push(colorToTailwindArbitrary(fill.color, "bg"));
    } else if (fill.type === "linearGradient") {
      // For simple 2-stop linear gradients, use Tailwind gradient utilities
      const normalized = normalizeGradientPaint(fill);
      if (normalized.stops.length === 2 && normalized.colorSpace === "rgb") {
        const from = colorToHex(normalized.stops[0]!.color);
        const to = colorToHex(normalized.stops[1]!.color);
        classes.push("bg-gradient-to-r");
        classes.push(`from-[${from}]`);
        classes.push(`to-[${to}]`);
      } else {
        classes.push("/* gradient fill: use CSS background */");
      }
    } else if (fill.type === "radialGradient") {
      classes.push("/* radial gradient fill: use CSS background */");
    } else if (fill.type === "image") {
      classes.push("/* image fill: use CSS background-image */");
    }
  } else if (fills.length > 1) {
    classes.push("/* multiple fills: use CSS background */");
  }

  // Stroke
  if (strokes.length > 0) {
    const stroke = strokes[0]!;
    if (stroke.paint.type === "solid") {
      const borderWidth = Math.round(stroke.width);
      const widthClass = borderWidth === 1 ? "border" : `border-[${fmtPx(stroke.width)}]`;
      classes.push(widthClass);
      classes.push(colorToTailwindArbitrary(stroke.paint.color, "border"));
    }
  }

  // Effects
  const shadows = effectsToCssBoxShadow(effects);
  if (shadows.length === 1) {
    const raw = shadows[0]!.replace(/,\s*/g, "_").replace(/\s+/g, "_");
    classes.push(`shadow-[${raw}]`);
  } else if (shadows.length > 1) {
    classes.push("/* multiple shadows: use CSS box-shadow */");
  }

  const filters = effectsToCssFilter(effects);
  if (filters.length === 1 && effects.find(e => e.type === "layerBlur" && e.visible)) {
    const layerBlurEffect = effects.find(e => e.type === "layerBlur" && e.visible) as { blur: number } | undefined;
    if (layerBlurEffect) {
      classes.push(`blur-[${fmtPx(layerBlurEffect.blur)}]`);
    }
  }

  const backdropFilters = effectsToCssBackdropFilter(effects);
  if (backdropFilters.length === 1) {
    const bgBlurEffect = effects.find(e => e.type === "backgroundBlur" && e.visible) as { blur: number } | undefined;
    if (bgBlurEffect) {
      classes.push(`backdrop-blur-[${fmtPx(bgBlurEffect.blur)}]`);
    }
  }

  // Opacity
  if (style.opacity < 1) {
    classes.push(closestTailwindOpacity(style.opacity));
  }

  // Corner radius
  const cornerRadii = primitiveCornerRadii(node);
  if (cornerRadii) {
    const [tl, tr, br, bl] = cornerRadii;
    const unique = new Set(cornerRadii);
    if (unique.size === 1) {
      classes.push(closestTailwindRounded(tl!));
    } else {
      classes.push(`rounded-tl-[${fmtPx(tl!)}]`);
      classes.push(`rounded-tr-[${fmtPx(tr!)}]`);
      classes.push(`rounded-br-[${fmtPx(br!)}]`);
      classes.push(`rounded-bl-[${fmtPx(bl!)}]`);
    }
  }

  // Auto layout
  if (node.type === "frame") {
    const mode = frameAutoLayoutMode(node);
    if (mode === "flex" && node.flexLayout) {
      const layout = node.flexLayout;
      classes.push("flex");
      const dirMap: Record<string, string> = {
        row: "flex-row",
        "row-reverse": "flex-row-reverse",
        column: "flex-col",
        "column-reverse": "flex-col-reverse",
      };
      classes.push(dirMap[layout.direction] ?? "flex-row");

      const justifyMap: Record<string, string> = {
        "flex-start": "justify-start",
        center: "justify-center",
        "flex-end": "justify-end",
        "space-between": "justify-between",
        "space-around": "justify-around",
        "space-evenly": "justify-evenly",
      };
      classes.push(justifyMap[layout.justifyContent] ?? "justify-start");

      const alignMap: Record<string, string> = {
        "flex-start": "items-start",
        center: "items-center",
        "flex-end": "items-end",
        stretch: "items-stretch",
      };
      classes.push(alignMap[layout.alignItems] ?? "items-start");

      if (layout.gap > 0) {
        const gapStandard: Record<number, string> = { 1: "gap-px", 4: "gap-1", 8: "gap-2", 12: "gap-3", 16: "gap-4", 20: "gap-5", 24: "gap-6" };
        classes.push(gapStandard[layout.gap] ?? `gap-[${fmtPx(layout.gap)}]`);
      }

      const { top, right, bottom, left } = layout.padding;
      if (top === right && right === bottom && bottom === left && top > 0) {
        const padStandard: Record<number, string> = { 4: "p-1", 8: "p-2", 12: "p-3", 16: "p-4", 20: "p-5", 24: "p-6" };
        classes.push(padStandard[top] ?? `p-[${fmtPx(top)}]`);
      } else if (top > 0 || right > 0 || bottom > 0 || left > 0) {
        if (top > 0) classes.push(`pt-[${fmtPx(top)}]`);
        if (right > 0) classes.push(`pr-[${fmtPx(right)}]`);
        if (bottom > 0) classes.push(`pb-[${fmtPx(bottom)}]`);
        if (left > 0) classes.push(`pl-[${fmtPx(left)}]`);
      }
    }
  }

  // Typography
  if (node.type === "text" && node.text) {
    const ts = node.text.style;
    if (ts.fontSize) {
      const sizeStandard: Record<number, string> = { 12: "text-xs", 14: "text-sm", 16: "text-base", 18: "text-lg", 20: "text-xl", 24: "text-2xl", 30: "text-3xl", 36: "text-4xl" };
      classes.push(sizeStandard[ts.fontSize] ?? `text-[${fmtPx(ts.fontSize)}]`);
    }
    if (ts.fontWeight) {
      const weightStandard: Record<number, string> = { 100: "font-thin", 200: "font-extralight", 300: "font-light", 400: "font-normal", 500: "font-medium", 600: "font-semibold", 700: "font-bold", 800: "font-extrabold", 900: "font-black" };
      classes.push(weightStandard[ts.fontWeight] ?? `font-[${ts.fontWeight}]`);
    }
    if (ts.horizontalAlign === "CENTER") classes.push("text-center");
    else if (ts.horizontalAlign === "RIGHT") classes.push("text-right");
    else if (ts.horizontalAlign === "JUSTIFIED") classes.push("text-justify");
    // Text color from first fill
    const textFills = normalizeFillStack(style);
    const firstFill = textFills[0];
    if (firstFill?.type === "solid") {
      classes.push(colorToTailwindArbitrary(firstFill.color, "text"));
    }
  }

  return classes.filter(c => !c.startsWith("/*"));
}

// --------------- Tailwind – fills only ---------------

export function generateFillsTailwind(node: SceneNode): string[] {
  const fills = normalizeFillStack(node.style);
  if (fills.length === 0) return [];
  const classes: string[] = [];
  if (fills.length === 1) {
    const fill = fills[0]!;
    if (fill.type === "solid") {
      classes.push(colorToTailwindArbitrary(fill.color, "bg"));
    } else if (fill.type === "linearGradient") {
      const normalized = normalizeGradientPaint(fill);
      if (normalized.stops.length === 2 && normalized.colorSpace === "rgb") {
        classes.push("bg-gradient-to-r");
        classes.push(`from-[${colorToHex(normalized.stops[0]!.color)}]`);
        classes.push(`to-[${colorToHex(normalized.stops[1]!.color)}]`);
      }
    }
  }
  return classes;
}

export function generateStrokesTailwind(node: SceneNode): string[] {
  const strokes = normalizeStrokeStack(node.style);
  if (strokes.length === 0) return [];
  const classes: string[] = [];
  const stroke = strokes[0]!;
  if (stroke.paint.type === "solid") {
    const borderWidth = Math.round(stroke.width);
    classes.push(borderWidth === 1 ? "border" : `border-[${fmtPx(stroke.width)}]`);
    classes.push(colorToTailwindArbitrary(stroke.paint.color, "border"));
  }
  return classes;
}

// --------------- Effects (separate section) ---------------

export function generateEffectsCss(node: SceneNode): string {
  const effects = (node.style.effects ?? []).filter(e => e.visible);
  if (effects.length === 0) return "/* no effects */";
  const lines: string[] = [];
  const shadows = effectsToCssBoxShadow(effects);
  if (shadows.length > 0) lines.push(`box-shadow: ${shadows.join(",\n  ")};`);
  const filters = effectsToCssFilter(effects);
  if (filters.length > 0) lines.push(`filter: ${filters.join(" ")};`);
  const backdropFilters = effectsToCssBackdropFilter(effects);
  if (backdropFilters.length > 0) lines.push(`backdrop-filter: ${backdropFilters.join(" ")};`);
  return lines.join("\n");
}

export function generateEffectsTailwind(node: SceneNode): string[] {
  const effects = (node.style.effects ?? []).filter(e => e.visible);
  if (effects.length === 0) return [];
  const classes: string[] = [];
  const shadows = effectsToCssBoxShadow(effects);
  if (shadows.length === 1) {
    classes.push(`shadow-[${shadows[0]!.replace(/,\s*/g, "_").replace(/\s+/g, "_")}]`);
  }
  const layerBlur = effects.find(e => e.type === "layerBlur") as { blur: number } | undefined;
  if (layerBlur) classes.push(`blur-[${fmtPx(layerBlur.blur)}]`);
  const bgBlur = effects.find(e => e.type === "backgroundBlur") as { blur: number } | undefined;
  if (bgBlur) classes.push(`backdrop-blur-[${fmtPx(bgBlur.blur)}]`);
  return classes;
}

// --------------- Typography (separate section) ---------------

export function generateTypographyCss(node: SceneNode): string {
  if (node.type !== "text") return "/* not a text node */";
  const ts = node.text.style;
  const lines: string[] = [];
  if (ts.fontFamily) lines.push(`font-family: "${ts.fontFamily}", sans-serif;`);
  if (ts.fontSize) lines.push(`font-size: ${fmtPx(ts.fontSize)};`);
  if (ts.fontWeight) lines.push(`font-weight: ${ts.fontWeight};`);
  if (ts.lineHeight) {
    let lh: string;
    if (ts.lineHeight === "auto") lh = "normal";
    else if (typeof ts.lineHeight === "number") lh = fmtPx(ts.lineHeight);
    else if (ts.lineHeight.unit === "px") lh = fmtPx(ts.lineHeight.value);
    else lh = `${fmt(ts.lineHeight.value)}%`;
    lines.push(`line-height: ${lh};`);
  }
  if (ts.letterSpacing !== undefined && ts.letterSpacing !== 0) {
    lines.push(`letter-spacing: ${fmt(ts.letterSpacing / (ts.fontSize ?? 12))}em;`);
  }
  const alignMap: Record<string, string> = { CENTER: "center", RIGHT: "right", JUSTIFIED: "justify" };
  const align = alignMap[ts.horizontalAlign];
  if (align) lines.push(`text-align: ${align};`);
  const firstFill = normalizeFillStack(node.style)[0];
  if (firstFill?.type === "solid") lines.push(`color: ${colorToHex(firstFill.color)};`);
  return lines.length > 0 ? lines.join("\n") : "/* no typography */";
}

export function generateTypographyTailwind(node: SceneNode): string[] {
  if (node.type !== "text") return [];
  const ts = node.text.style;
  const classes: string[] = [];
  if (ts.fontSize) {
    const sizeMap: Record<number, string> = { 12: "text-xs", 14: "text-sm", 16: "text-base", 18: "text-lg", 20: "text-xl", 24: "text-2xl", 30: "text-3xl", 36: "text-4xl" };
    classes.push(sizeMap[ts.fontSize] ?? `text-[${fmtPx(ts.fontSize)}]`);
  }
  if (ts.fontWeight) {
    const weightMap: Record<number, string> = { 100: "font-thin", 200: "font-extralight", 300: "font-light", 400: "font-normal", 500: "font-medium", 600: "font-semibold", 700: "font-bold", 800: "font-extrabold", 900: "font-black" };
    classes.push(weightMap[ts.fontWeight] ?? `font-[${ts.fontWeight}]`);
  }
  if (ts.horizontalAlign === "CENTER") classes.push("text-center");
  else if (ts.horizontalAlign === "RIGHT") classes.push("text-right");
  else if (ts.horizontalAlign === "JUSTIFIED") classes.push("text-justify");
  const firstFill = normalizeFillStack(node.style)[0];
  if (firstFill?.type === "solid") classes.push(colorToTailwindArbitrary(firstFill.color, "text"));
  return classes;
}

export function generateFillsOnlyCss(node: SceneNode): string {
  const fills = normalizeFillStack(node.style);
  if (fills.length === 0) return "/* no fills */";
  if (fills.length === 1) {
    const bg = paintToCssBackground(fills[0]!);
    const prop = fills[0]!.type === "solid" ? "background-color" : "background";
    return bg ? `${prop}: ${bg};` : "/* unsupported fill */";
  }
  const bgs = fills.map(f => paintToCssBackground(f)).filter(Boolean) as string[];
  return `background: ${bgs.join(",\n  ")};`;
}

export function generateStrokesOnlyCss(node: SceneNode): string {
  const strokes = normalizeStrokeStack(node.style);
  if (strokes.length === 0) return "/* no strokes */";
  const lines: string[] = [];
  for (const stroke of strokes) {
    const paint = stroke.paint;
    if (paint.type === "solid") {
      const color = colorToHex(paint.color);
      const align = stroke.align ?? "center";
      if (align === "center") {
        lines.push(`border: ${fmtPx(stroke.width)} solid ${color};`);
      } else if (align === "inside") {
        lines.push(`box-shadow: inset 0 0 0 ${fmtPx(stroke.width)} ${color};`);
      } else if (align === "outside") {
        lines.push(`outline: ${fmtPx(stroke.width)} solid ${color};`);
        lines.push(`outline-offset: 0;`);
      }
    } else if (paint.type === "linearGradient") {
      lines.push(`/* gradient stroke: ${linearGradientToCss(paint)} */`);
    }
  }
  return lines.join("\n");
}
